import { useQuery } from '@tanstack/react-query'
import LabelToggle, {
  LabelToggleOption,
} from 'ext-ui/components/LabelToggle/LabelToggle'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import ReportingDateRangeControl from 'src/app/Reporting/components/ReportingDateRangeControl'
import {
  createDateRangeFromSearch,
  createDefaultDateRange,
  ReportingDateRange,
} from 'src/app/Reporting/components/reportingPanelUtils'
import SpendByAccountingCategoryPanel from 'src/app/Reporting/components/SpendByAccountingCategoryPanel'
import SpendByDepartmentPanel from 'src/app/Reporting/components/SpendByDepartmentPanel'
import SpendByPersonPanel from 'src/app/Reporting/components/SpendByPersonPanel'
import SpendByVendorPanel from 'src/app/Reporting/components/SpendByVendorPanel'
import TotalCompanySpendPanel from 'src/app/Reporting/components/TotalCompanySpendPanel'
import * as style from 'src/app/Reporting/Reporting.style'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { ExtendCurrency } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { homeUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { orgCurrencyQueries } from 'src/queries/organizations/currencies/currencies.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { isSpendReportingDashboardEnabled } from 'src/utils/featureFlags'

// Display order: USD first, then CAD.
const currencyValues: ExtendCurrency[] = [
  ExtendCurrency.Usd,
  ExtendCurrency.Cad,
]

const Reporting: React.FC = () => {
  const translate = useTranslate()
  const history = useHistory()
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const { data: currentUser, isLoading: isUserLoading } = useQueryOwnUser({})
  const reportingEnabled = isSpendReportingDashboardEnabled(
    remoteConfig,
    currentUser?.organizationId,
  )
  const shouldFetchReportingFeature =
    !!reportingEnabled && !!currentUser?.organizationId
  const { data: reportingFeatures, isLoading: isReportingFeatureLoading } =
    useQueryUserFeatures([UserFeature.VIEW_REPORTING], {
      enabled: shouldFetchReportingFeature,
    })
  // Must not be gated on `reportingEnabled`: it derives from
  // `currentUser?.organizationId`, which is undefined while the user loads, so
  // gating here would let the redirect effect fire before access is resolved.
  const isAccessLoading =
    isUserLoading || (shouldFetchReportingFeature && isReportingFeatureLoading)
  const canViewReporting =
    !!reportingEnabled && reportingFeatures?.VIEW_REPORTING === true

  useEffect(() => {
    if (isAccessLoading || canViewReporting) return
    history.replace(homeUrl())
  }, [canViewReporting, history, isAccessLoading])

  const [dateRange, setDateRange] = useState<ReportingDateRange>(() =>
    typeof window === 'undefined'
      ? createDefaultDateRange()
      : (createDateRangeFromSearch(window.location.search) ??
        createDefaultDateRange()),
  )
  const [selectedCurrency, setSelectedCurrency] = useState<ExtendCurrency>(
    ExtendCurrency.Usd,
  )

  // Single page-level range: merge each edit into the latest range so a custom
  // calendar pick (two back-to-back partial emits) applies to the live value.
  const handleDateRangeChange = useCallback(
    (range: Partial<ReportingDateRange>) => {
      setDateRange((current) => ({ ...current, ...range }))
    },
    [],
  )

  const handleCurrencyChange = useCallback((option: LabelToggleOption) => {
    setSelectedCurrency(option.value as ExtendCurrency)
  }, [])

  const currencyOptions = useMemo<LabelToggleOption[]>(
    () =>
      currencyValues.map((currency) => ({
        displayName: translate(currency),
        value: currency,
      })),
    [translate],
  )

  const organizationId = currentUser?.organizationId

  const { data: orgCurrencies } = useQuery({
    ...orgCurrencyQueries.list(organizationId),
    enabled: canViewReporting && !!organizationId,
  })

  const showCurrencyToggle = useMemo(() => {
    if (!orgCurrencies) return false
    const codes = orgCurrencies.map((c) => c.currencyCode.toUpperCase())
    return (
      codes.includes(ExtendCurrency.Usd) && codes.includes(ExtendCurrency.Cad)
    )
  }, [orgCurrencies])

  useEffect(() => {
    if (!showCurrencyToggle) setSelectedCurrency(ExtendCurrency.Usd)
  }, [showCurrencyToggle])

  const sharedPanelProps = useMemo(
    () => ({
      organizationId,
      currency: selectedCurrency,
      enabled: canViewReporting,
      dateRange,
    }),
    [canViewReporting, dateRange, organizationId, selectedCurrency],
  )

  if (isAccessLoading) {
    return <LoadingBackgroundPage />
  }

  if (!canViewReporting) {
    return null
  }

  return (
    <>
      <Helmet>
        <title>{translate('Reporting')}</title>
      </Helmet>
      <LayoutPage
        title={<span>{translate('Reporting')}</span>}
        subtitle={
          <span css={style.subtitle}>
            {translate(
              'Review company spend across accounts, teams, merchants, and people.',
            )}
          </span>
        }
        actions={
          <div css={style.toolbar} data-testid="reporting-toolbar">
            <ReportingDateRangeControl
              dateRange={dateRange}
              onChange={handleDateRangeChange}
            />
            {showCurrencyToggle && (
              <LabelToggle
                name="reporting-currency"
                role="radiogroup"
                aria-label={translate('Display currency')}
                options={currencyOptions}
                value={selectedCurrency}
                onChange={handleCurrencyChange}
              />
            )}
          </div>
        }
      >
        <div css={style.page}>
          <div css={style.chartsGrid} data-testid="reporting-breakdown-grid">
            <TotalCompanySpendPanel {...sharedPanelProps} />
            <SpendByPersonPanel {...sharedPanelProps} />
            <SpendByAccountingCategoryPanel {...sharedPanelProps} />
            <SpendByDepartmentPanel {...sharedPanelProps} />
            <SpendByVendorPanel {...sharedPanelProps} />
          </div>
        </div>
      </LayoutPage>
    </>
  )
}

export default Reporting
